import { dirname } from "node:path";
import { ComponentCompileError } from "./errors.js";
import type {
  ArtifactDirection,
  BindingIR,
  ComponentIR,
  JSONSchema,
  JSONSchemaRef,
  OperationIR,
  OptionIR,
  RiskLevel,
} from "./ir.js";
import { parseComponentFile } from "./parser.js";
import { resolveRefs } from "./ref-resolver.js";
import {
  SUPPORTED_SCHEMA_VERSIONS,
  type ComponentDSL,
  type JSONSchemaRefDSL,
  type OperationDSL,
  type OptionDSL,
} from "./schema.js";
import { validateComponent } from "./validation.js";

export { ComponentCompileError };

function assertSupportedSchemaVersion(schema: string): void {
  if (
    !(SUPPORTED_SCHEMA_VERSIONS as readonly string[]).includes(schema)
  ) {
    throw new ComponentCompileError(
      `Unsupported schema version "${schema}". Supported: ${SUPPORTED_SCHEMA_VERSIONS.join(", ")}`,
    );
  }
}

function toJSONSchemaRef(
  ref: JSONSchemaRefDSL,
  basePath?: string,
): JSONSchemaRef {
  let value: unknown = ref;
  if (basePath) {
    value = resolveRefs(ref, { basePath });
  }

  if (value && typeof value === "object" && !Array.isArray(value)) {
    const obj = value as Record<string, unknown>;
    if ("schema" in obj && obj.schema && typeof obj.schema === "object") {
      return { schema: obj.schema as JSONSchema };
    }
    if ("$ref" in obj && typeof obj["$ref"] === "string") {
      return { $ref: obj["$ref"] };
    }
    return obj as JSONSchema;
  }
  return ref as JSONSchemaRef;
}

function normalizeArtifactSlots(
  slots: OperationDSL["artifactSlots"],
): OperationIR["artifactSlots"] {
  if (!slots) {
    return {};
  }

  const result: OperationIR["artifactSlots"] = {};
  for (const [slotName, value] of Object.entries(slots)) {
    if (typeof value === "string") {
      result[slotName] = { artifact: value, direction: "readwrite" };
    } else {
      result[slotName] = {
        artifact: value.artifact,
        direction: value.direction as ArtifactDirection,
      };
    }
  }
  return result;
}

function asSchemaObject(ref: JSONSchemaRef | undefined): JSONSchema | undefined {
  if (!ref) {
    return undefined;
  }
  if ("schema" in ref && ref.schema && typeof ref.schema === "object") {
    return ref.schema as JSONSchema;
  }
  if (!("$ref" in ref)) {
    return ref as JSONSchema;
  }
  return undefined;
}

/**
 * Extract failure class names from a resolved failed output schema.
 *
 * Checks, in order:
 * 1. Top-level `failure_classes` array
 * 2. `properties.failure_class.enum`
 * 3. `oneOf` entries with `const` or `properties.failure_class.const`
 */
export function extractFailureClasses(
  failedRef: JSONSchemaRef | undefined,
): string[] {
  const schema = asSchemaObject(failedRef);
  if (!schema) {
    return [];
  }

  const failureClasses = schema["failure_classes"];
  if (Array.isArray(failureClasses)) {
    return failureClasses.filter((v): v is string => typeof v === "string");
  }

  const properties = schema["properties"];
  if (properties && typeof properties === "object") {
    const failureClass = (properties as Record<string, unknown>)["failure_class"];
    if (failureClass && typeof failureClass === "object") {
      const enumValues = (failureClass as Record<string, unknown>)["enum"];
      if (Array.isArray(enumValues)) {
        return enumValues.filter((v): v is string => typeof v === "string");
      }
    }
  }

  const oneOf = schema["oneOf"];
  if (Array.isArray(oneOf)) {
    const classes: string[] = [];
    for (const variant of oneOf) {
      if (!variant || typeof variant !== "object") {
        continue;
      }
      const obj = variant as Record<string, unknown>;
      if (typeof obj["const"] === "string") {
        classes.push(obj["const"]);
        continue;
      }
      const props = obj["properties"];
      if (props && typeof props === "object") {
        const fc = (props as Record<string, unknown>)["failure_class"];
        if (fc && typeof fc === "object") {
          const c = (fc as Record<string, unknown>)["const"];
          if (typeof c === "string") {
            classes.push(c);
          }
        }
      }
    }
    if (classes.length > 0) {
      return classes;
    }
  }

  return [];
}

function compileOption(opt: OptionDSL): OptionIR {
  return {
    name: opt.name,
    schema: opt.schema,
    required: opt.required ?? false,
    description: opt.description,
  };
}

function compileOperation(op: OperationDSL, basePath?: string): OperationIR {
  const output = {
    success: toJSONSchemaRef(op.output.success, basePath),
    failed: op.output.failed
      ? toJSONSchemaRef(op.output.failed, basePath)
      : undefined,
  };

  return {
    description: op.description,
    agentWorkflow: op.agentWorkflow,
    agentTask: op.agentTask,
    handler: op.handler,
    options: (op.options ?? []).map(compileOption),
    input: op.input ? toJSONSchemaRef(op.input, basePath) : undefined,
    output,
    errorModel: {
      failureClasses: extractFailureClasses(output.failed),
    },
    artifactSlots: normalizeArtifactSlots(op.artifactSlots),
    riskLevel: (op.riskLevel ?? "medium") as RiskLevel,
    idempotent: op.idempotent ?? false,
    requiresConfirmation: op.requiresConfirmation ?? false,
    execution: {
      timeoutMs: op.execution?.timeoutMs,
      costCeilingUsd: op.execution?.costCeilingUsd,
    },
    memoryRef: op.memoryRef,
  };
}

export interface CompileComponentOptions {
  basePath?: string;
  /** When true (default), validate implementation file and agent workflow/task IDs. */
  validate?: boolean;
}

/**
 * Compile a validated Component DSL into Component IR.
 */
export function compileComponent(
  dsl: ComponentDSL,
  options: CompileComponentOptions = {},
): ComponentIR {
  assertSupportedSchemaVersion(dsl.schema);

  const operations: Record<string, OperationIR> = {};
  for (const [name, op] of Object.entries(dsl.operations)) {
    operations[name] = compileOperation(op, options.basePath);
  }

  return {
    schema: dsl.schema,
    info: {
      id: dsl.info.id,
      version: dsl.info.version,
      description: dsl.info.description,
    },
    implementation: dsl.implementation,
    embeddedDslDir: dsl.embedded_dsl_dir,
    projections: [...dsl.projections],
    governance: dsl.governance
      ? {
          guardrails: dsl.governance.guardrails,
          observability: dsl.governance.observability,
        }
      : undefined,
    bindings: dsl.bindings ? compileBindings(dsl.bindings) : undefined,
    operations,
  };
}

function compileBindings(
  bindings: NonNullable<ComponentDSL["bindings"]>,
): Record<string, BindingIR> {
  const result: Record<string, BindingIR> = {};
  for (const [id, binding] of Object.entries(bindings)) {
    result[id] = {
      package: binding.package,
      command: binding.command ?? id,
      config: binding.config ?? {},
    };
  }
  return result;
}

async function maybeValidateComponent(
  ir: ComponentIR,
  options: CompileComponentOptions,
): Promise<void> {
  if (options.validate === false) {
    return;
  }
  if (!options.basePath) {
    return;
  }
  await validateComponent(ir, { basePath: options.basePath });
}

/**
 * Compile a validated Component DSL into Component IR, optionally validating
 * implementation references.
 */
export async function compileComponentAsync(
  dsl: ComponentDSL,
  options: CompileComponentOptions = {},
): Promise<ComponentIR> {
  const ir = compileComponent(dsl, options);
  await maybeValidateComponent(ir, options);
  return ir;
}

/**
 * Parse a component YAML file and compile it to IR.
 */
export async function compileComponentFile(
  filePath: string,
  options: Omit<CompileComponentOptions, "basePath"> = {},
): Promise<ComponentIR> {
  const basePath = dirname(filePath);
  const dsl = await parseComponentFile(filePath);
  const ir = compileComponent(dsl, { ...options, basePath });
  await maybeValidateComponent(ir, { ...options, basePath });
  return ir;
}
